import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { matchesString } from "LIB/filters";
import {
  FilterSelect,
  Pagination,
  SearchFilter,
  TitleWithFilters
} from "MODULES/shared/components/Pagination";
import useCurrentUser from "APP_ROOT/hooks/useCurrentUser";
import { QC_HOLD, STORE_LOCATION_ID } from "MODULES/inventories/constants";
import { Search } from "MODULES/shared/components";
import { CLEAR_FILTERS_TEXT } from "APP_ROOT/constants";
import DateRangePicker from "MODULES/shared/components/DateRangePicker/DateRangePicker";
import DecoratedExportButton from "MODULES/Receiving/Search/ExportButton";
import Table from "./Table";
import { receivingRecordsSearch } from "../pagination";
import { subscribe, unsubscribe } from "../actions";

const filters = [
  {
    label: "My Received Orders",
    filters: { current_user_buyer: true }
  },
  {
    label: "Awaiting QC Review",
    filters: { qc_status: "under_review" }
  },
  {
    label: "Active QC Holds",
    filters: { qc_status: "qc_hold" }
  },
  {
    label: "Active Discrepancies",
    filters: { all_discrepancies: undefined, open_discrepancies: true }
  },
  {
    label: "Consignments",
    filters: { consignment: true }
  },
  {
    label: "EMAs",
    filters: { ema: true }
  },
  {
    label: "Show Partially Received",
    filters: { partially_received: true }
  },
  {
    label: "All ROs",
    filters: { receiving_type: "ro" }
  },
  {
    label: "All SOs",
    filters: { receiving_type: "so" }
  },
  {
    label: "All POs",
    filters: { receiving_type: "po" }
  },
  {
    label: "All LPOs",
    filters: { receiving_type: "lot_line_item" }
  },
  {
    label: "All RMAs",
    filters: { receiving_type: "rma" }
  },
  {
    label: "All Demerits",
    filters: { active_demerits: true }
  },
  {
    label: "All Discrepancies",
    filters: { all_discrepancies: true, open_discrepancies: undefined }
  },
  {
    label: "Missing ECCN",
    filters: { missing_eccn: true }
  },
  {
    label: "Received By Me",
    filters: { current_user: true }
  },
  {
    label: "Reserved For Sale",
    filters: { purchase_for_sale: true }
  },
  {
    label: "Dropshipments",
    filters: { dropshipment: true },
    hr: true
  },
  {
    label: CLEAR_FILTERS_TEXT,
    filters: {
      all_discrepancies: undefined,
      current_user: undefined,
      current_user_buyer: undefined,
      consignment: undefined,
      dropshipment: undefined,
      ema: undefined,
      end_date: undefined,
      qc_hold: undefined,
      qc_status: undefined,
      open_discrepancies: undefined,
      receiving_type: undefined,
      store_location: undefined,
      active_demerits: undefined,
      start_date: undefined,
      store_location_id: undefined,
      missing_eccn: undefined,
      partially_received: undefined,
      purchase_for_sale: undefined
    }
  }
];

const filterTags = [
  {
    label: "All Discrepancies",
    key: "all_discrepancies"
  },
  {
    label: "Received By Me",
    key: "current_user"
  },
  {
    label: "My Received Orders",
    key: "current_user_buyer"
  },
  {
    label: "Active Discrepancies",
    key: "open_discrepancies"
  },
  {
    label: "Awaiting QC Review",
    key: "qc_status",
    matchesTarget: matchesString("under_review")
  },
  {
    label: "Show Partially Received",
    key: "partially_received"
  },
  {
    label: "All ROs",
    key: "receiving_type",
    matchesTarget: matchesString("ro")
  },
  {
    label: "All SOs",
    key: "receiving_type",
    matchesTarget: matchesString("so")
  },
  {
    label: "All POs",
    key: "receiving_type",
    matchesTarget: matchesString("po")
  },
  {
    label: "All LPOs",
    key: "receiving_type",
    matchesTarget: matchesString("lot_line_item")
  },
  {
    label: "All RMAs",
    key: "receiving_type",
    matchesTarget: matchesString("rma")
  },
  {
    label: "Consignment",
    key: "consignment"
  },
  {
    label: "EMA",
    key: "ema"
  },
  {
    label: "Active QC Holds",
    key: "qc_status",
    matchesTarget: matchesString(QC_HOLD)
  },
  {
    label: "Missing ECCN",
    key: "missing_eccn"
  },
  {
    label: "Dropshipments",
    key: "dropshipment"
  },
  {
    label: "Reserved For Sale",
    key: "purchase_for_sale"
  }
];

function ReceivingRecordsSearch({
  pushHistory,
  subscribeToReceivingChannel,
  unsubscribeToReceivingChannel
}) {
  const currentUser = useCurrentUser();

  const filtersWithLocation = currentUser.locations.map(l => ({
    label: `Store Location = ${l.name}`,
    filters: { store_location_id: l.id }
  }));

  const filterTagsWithLocation = currentUser.locations.map(l => ({
    label: `Store Location = ${l.name}`,
    key: STORE_LOCATION_ID,
    matchesTarget: matchesString(l.id)
  }));

  useEffect(() => {
    subscribeToReceivingChannel();

    return function cleanup() {
      unsubscribeToReceivingChannel();
    };
  }, []);

  return (
    <Search search={receivingRecordsSearch} syncWithUrl>
      {searchProps => (
        <div>
          <div className="form-group form-group-searchbar has-feedback has-feedback-left">
            <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
            <div className="input-group">
              <SearchFilter
                {...searchProps}
                placeholder="Search by typing a part number, company name, serial number, received date or any ref. number..."
                submitOnSingleResult={r => pushHistory(`/receiving/${r.uuid}`)}
              />
              <DecoratedExportButton searchProps={searchProps} />
              <DateRangePicker {...searchProps} />
              <FilterSelect
                {...searchProps}
                filters={filtersWithLocation.concat(filters)}
                right
              />
            </div>
          </div>
          <TitleWithFilters
            {...searchProps}
            title="Received Orders"
            filters={filterTagsWithLocation.concat(filterTags)}
          />
          <Table {...searchProps} />
          <Pagination {...searchProps} />
        </div>
      )}
    </Search>
  );
}

ReceivingRecordsSearch.propTypes = {
  pushHistory: PropTypes.func.isRequired
};

export default connect(undefined, dispatch => ({
  subscribeToReceivingChannel: () => dispatch(subscribe()),
  unsubscribeToReceivingChannel: () => dispatch(unsubscribe()),
  pushHistory: path => dispatch(push(path))
}))(ReceivingRecordsSearch);



// WEBPACK FOOTER //
// ./app/modules/Receiving/Search/ReceivingSearch.jsx